import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import { Camera, AlertTriangle, X, Loader2, Phone } from "lucide-react";

// PUBLIC page — no AppShell, no login. Token comes from ?t=<magic-link>.
// Themed after Mirzapur / Bhadohi handloom weaving (indigo, terracotta,
// saffron, parchment). All commercial fields are stripped server-side.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Small SVG paisley/mango motif — classic Bhadohi rug medallion
const Paisley = ({ size = 56, className = "" }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} className={className} aria-hidden="true">
    <g fill="none" stroke="currentColor" strokeWidth="1.2">
      <path d="M32 6c14 4 20 18 14 32-4 10-14 14-24 12" strokeLinecap="round" />
      <path d="M32 14c9 3 13 12 9 21-3 6-10 9-16 7" strokeLinecap="round" />
      <path d="M32 22c5 2 7 7 5 12-2 4-6 6-10 5" strokeLinecap="round" />
      <circle cx="32" cy="30" r="2.4" fill="currentColor" />
      <path d="M8 32q6 6 12 0" strokeLinecap="round" />
      <path d="M8 42q6 6 12 0" strokeLinecap="round" />
    </g>
  </svg>
);

function fileToDataUrl(file, maxKB = 400) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        // Downscale to keep under ~maxKB
        const canvas = document.createElement("canvas");
        const maxDim = 1024;
        const ratio = Math.min(1, maxDim / Math.max(img.width, img.height));
        canvas.width = Math.round(img.width * ratio);
        canvas.height = Math.round(img.height * ratio);
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        let q = 0.75;
        let out = canvas.toDataURL("image/jpeg", q);
        while (out.length / 1024 > maxKB && q > 0.35) {
          q -= 0.1;
          out = canvas.toDataURL("image/jpeg", q);
        }
        resolve(out);
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function StatusChip({ flag, t }) {
  if (flag === "RED") return <span className="status-chip mirzapur-chip-red" data-testid="chip-overdue">● {t("Overdue", "बकाया")}</span>;
  if (flag === "AMBER") return <span className="status-chip mirzapur-chip-amber" data-testid="chip-due-soon">● {t("Due soon", "जल्द बकाया")}</span>;
  return <span className="status-chip mirzapur-chip-green" data-testid="chip-on-time">● {t("On time", "समय पर")}</span>;
}

function UpdatePanel({ line, token, lang, onDone, onCancel }) {
  const [status, setStatus] = useState("In production");
  const [expectedDate, setExpectedDate] = useState("");
  const [note, setNote] = useState("");
  const [photos, setPhotos] = useState([]); // data-url strings
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);
  const t = (en, hi) => (lang === "hi" ? hi : en);

  const addPhotos = async (fileList) => {
    setErr("");
    const list = Array.from(fileList || []).slice(0, 3 - photos.length);
    const next = [];
    for (const f of list) {
      try { next.push(await fileToDataUrl(f)); } catch { setErr(t("Photo error", "फ़ोटो त्रुटि")); }
    }
    setPhotos((p) => [...p, ...next].slice(0, 3));
  };

  const submit = async () => {
    setErr("");
    if (status === "Delayed" && !expectedDate) { setErr(t("Delayed → expected date required", "देरी → अपेक्षित तिथि आवश्यक")); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/portal/vendor/update`, {
        client_order_line_id: line.id,
        status, expected_date: expectedDate || null,
        note, photos,
      }, { params: { token } });
      onDone();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-3 border-t border-[#23375E]/10 pt-3" data-testid={`portal-update-panel-${line.id}`}>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div className="sm:col-span-1">
          <label className="mirzapur-eyebrow block mb-1">{t("Status", "स्थिति")}</label>
          <select data-testid={`portal-status-${line.id}`} value={status} onChange={(e) => setStatus(e.target.value)}
            className="w-full h-11 px-2 border border-[#23375E]/15 rounded-md text-sm bg-white">
            <option value="In production">{t("In production", "उत्पादन में")}</option>
            <option value="Ready at vendor end">{t("Ready at vendor end", "वेंडर के पास तैयार")}</option>
            <option value="Delayed">{t("Delayed", "देरी")}</option>
          </select>
        </div>
        {status === "Delayed" && (
          <div>
            <label className="mirzapur-eyebrow block mb-1">{t("Expected date", "अपेक्षित तिथि")}</label>
            <input data-testid={`portal-date-${line.id}`} type="date" value={expectedDate}
              onChange={(e) => setExpectedDate(e.target.value)}
              className="w-full h-11 px-2 border border-[#23375E]/15 rounded-md text-sm bg-white" />
          </div>
        )}
        <div className={status === "Delayed" ? "" : "sm:col-span-2"}>
          <label className="mirzapur-eyebrow block mb-1">{t("Note", "टिप्पणी")}</label>
          <input data-testid={`portal-note-${line.id}`} value={note} onChange={(e) => setNote(e.target.value)}
            placeholder={t("Add remark (optional)", "टिप्पणी जोड़ें (वैकल्पिक)")}
            className="w-full h-11 px-2 border border-[#23375E]/15 rounded-md text-sm bg-white" />
        </div>
      </div>

      <div className="mt-3">
        <label className="mirzapur-eyebrow block mb-1">{t("Photos (max 3)", "फोटो (अधिकतम 3)")}</label>
        <div className="flex flex-wrap gap-2 items-center">
          {photos.map((p, i) => (
            <div key={i} className="relative w-20 h-20 rounded-md overflow-hidden border border-[#23375E]/15" data-testid={`portal-photo-${line.id}-${i}`}>
              <img src={p} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => setPhotos((prev) => prev.filter((_, j) => j !== i))}
                className="absolute top-0 right-0 bg-black/60 text-white rounded-bl-md p-0.5" aria-label="remove">
                <X size={12} />
              </button>
            </div>
          ))}
          {photos.length < 3 && (
            <button type="button" data-testid={`portal-add-photo-${line.id}`}
              onClick={() => fileRef.current?.click()}
              className="w-20 h-20 rounded-md border-2 border-dashed border-[#23375E]/25 flex items-center justify-center text-[#23375E]/70 hover:bg-white">
              <Camera size={20} />
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple
            className="hidden" onChange={(e) => addPhotos(e.target.files)} />
        </div>
      </div>

      {err && <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-2" data-testid={`portal-update-err-${line.id}`}>{err}</div>}

      <div className="mt-3 flex flex-wrap gap-2">
        <button data-testid={`portal-submit-${line.id}`} disabled={busy}
          onClick={submit} className="mirzapur-btn-primary inline-flex items-center gap-2">
          {busy && <Loader2 size={14} className="animate-spin" />} {t("Send update", "अपडेट भेजें")}
        </button>
        <button data-testid={`portal-cancel-${line.id}`} onClick={onCancel} className="mirzapur-btn-secondary">
          {t("Cancel", "रद्द")}
        </button>
      </div>
    </div>
  );
}

export default function VendorPortal() {
  const [params] = useSearchParams();
  const token = params.get("t") || params.get("token") || "";
  const remind = params.get("remind") === "1";
  const { lang, toggle } = useLang();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [openId, setOpenId] = useState("");
  const [toast, setToast] = useState("");
  const [flagBusy, setFlagBusy] = useState("");
  const t = (en, hi) => (lang === "hi" ? hi : en);

  const load = () => {
    setErr(null);
    axios.get(`${API}/portal/vendor`, { params: { token } })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || e.message));
  };
  useEffect(() => {
    if (!token) { setErr(t("Missing token in URL", "URL में टोकन नहीं")); return; }
    load();
    // eslint-disable-next-line
  }, [token]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(""), 3200);
    return () => clearTimeout(timer);
  }, [toast]);

  const flagProblem = async (line) => {
    const n = window.prompt(t("What's the problem?", "क्या समस्या है?"));
    if (!n) return;
    setFlagBusy(line.id);
    try {
      await axios.post(`${API}/portal/vendor/flag`, { client_order_line_id: line.id, note: n }, { params: { token } });
      setToast(t("Problem flagged to HAG", "HAG को समस्या भेज दी गई"));
    } catch (e) { setToast(e?.response?.data?.detail || e.message); }
    finally { setFlagBusy(""); }
  };

  const submitDone = () => {
    setToast(t("Update sent — thank you", "अपडेट भेज दिया गया — धन्यवाद"));
    setOpenId("");
    load();
  };

  if (err) return (
    <div className="min-h-screen flex items-center justify-center p-6 mirzapur-bg" data-testid="portal-err">
      <div className="max-w-md text-center mirzapur-card p-8">
        <div className="mirzapur-stripe mb-5" />
        <div className="text-xl font-bold mirzapur-title mb-2">{t("Link expired or invalid", "लिंक अमान्य")}</div>
        <div className="text-sm text-[#4A627A]">{err}</div>
        <div className="text-xs text-[#7C8E9E] mt-3">{t("Please contact HAG for a new link.", "नया लिंक HAG से लें।")}</div>
        <a href="tel:+915443000000" className="mt-5 inline-flex items-center gap-2 mirzapur-btn-primary" data-testid="portal-err-contact">
          <Phone size={14} /> {t("Call HAG", "HAG को कॉल करें")}
        </a>
      </div>
    </div>
  );

  if (!data) return (
    <div className="min-h-screen mirzapur-bg flex items-center justify-center">
      <Loader2 className="animate-spin text-[#23375E]" />
    </div>
  );

  return (
    <div className="min-h-screen mirzapur-bg" data-testid="vendor-portal">
      {/* Header ribbon */}
      <div className="mirzapur-stripe" />
      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="text-[#B34A2C]"><Paisley size={52} /></div>
            <div>
              <div className="mirzapur-eyebrow" data-testid="portal-eyebrow">HAG · Mirzapur — Vendor Portal</div>
              <div className="text-2xl sm:text-3xl font-bold mirzapur-title" data-testid="portal-vendor-name">
                {lang === "hi" ? "नमस्ते" : "Namaste"}, {data.vendor_name}
              </div>
              <div className="text-xs text-[#4A627A] mt-1">
                {t("Your open lines with HAG · handloom weaves of Bhadohi", "HAG के साथ आपकी लंबित लाइनें · भदोही के हस्तनिर्मित कालीन")}
              </div>
            </div>
          </div>
          <button data-testid="portal-lang" onClick={toggle}
            className="text-xs uppercase tracking-widest px-3 py-2 rounded-full border border-[#23375E]/20 bg-white">
            {lang === "en" ? "हिन्दी" : "English"}
          </button>
        </div>

        {remind && (
          <div className="mirzapur-card p-4 mb-4" data-testid="portal-reminder-banner" style={{ borderLeft: "4px solid var(--mirzapur-terracotta)" }}>
            <div className="mirzapur-eyebrow">{t("Weekly reminder", "साप्ताहिक याद")}</div>
            <div className="text-sm mt-1" style={{ color: "var(--mirzapur-indigo)" }}>
              {t("This is your weekly reminder from HAG. Please update the status of your open lines below.",
                 "यह HAG की साप्ताहिक याद है। कृपया अपनी लंबित लाइनों की स्थिति नीचे अपडेट करें।")}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mb-3">
          <div className="text-xs text-[#4A627A]">{t("Open lines", "लंबित लाइनें")} · <b data-testid="portal-line-count">{data.lines.length}</b></div>
          <div className="text-[10px] text-[#7C8E9E]">
            {t("Link valid until", "लिंक की मान्यता")} {new Date(data.expires_at).toLocaleDateString(lang === "hi" ? "hi-IN" : "en-IN")}
          </div>
        </div>

        {toast && (
          <div className="fixed left-1/2 -translate-x-1/2 bottom-6 z-50 mirzapur-card px-4 py-2 text-sm" data-testid="portal-toast"
               style={{ color: "var(--mirzapur-indigo)" }}>
            {toast}
          </div>
        )}

        <div className="space-y-3">
          {data.lines.map((l) => {
            const isOpen = openId === l.id;
            return (
              <div key={l.id} className="mirzapur-card p-4" data-testid={`portal-line-${l.id}`}>
                <div className="flex justify-between flex-wrap gap-2 items-start">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-base font-semibold mirzapur-title" data-testid={`portal-sku-${l.id}`}>{l.sku}</div>
                      <StatusChip flag={l.urgency_flag} t={t} />
                    </div>
                    <div className="text-xs text-[#4A627A] mt-0.5">{l.sku_description}</div>
                    <div className="text-xs text-[#7C8E9E] mt-1">
                      {t("Size", "साइज़")}: {l.size_text || "—"} · {t("Colour", "रंग")}: {l.colour_attribute || "—"}
                    </div>
                    <div className="text-xs text-[#7C8E9E] mt-1">
                      {t("Outstanding", "बकाया")}: <b>{l.outstanding_qty}</b> · {t("Ship by", "भेजने की तिथि")}: {l.latest_ship_date ? new Date(l.latest_ship_date).toLocaleDateString(lang === "hi" ? "hi-IN" : "en-IN") : "—"}
                    </div>
                  </div>
                </div>

                {l.last_update && (
                  <div className="mt-2 text-xs bg-[#F4EBDA] border border-[#E1A24C]/40 rounded-md px-2 py-1" data-testid={`portal-last-${l.id}`}>
                    <span className="mirzapur-eyebrow mr-2">{t("Last update", "पिछला अपडेट")}</span>
                    <b>{l.last_update.status}</b>
                    {l.last_update.expected_date ? ` · ${t("expected", "अपेक्षित")} ${new Date(l.last_update.expected_date).toLocaleDateString()}` : ""}
                    {l.last_update.note ? ` — ${l.last_update.note}` : ""}
                  </div>
                )}

                {!isOpen ? (
                  <div className="mt-3 flex gap-2 flex-wrap">
                    <button data-testid={`portal-update-btn-${l.id}`} onClick={() => setOpenId(l.id)}
                      className="mirzapur-btn-primary">
                      {t("Update status", "स्थिति अपडेट करें")}
                    </button>
                    <button data-testid={`portal-flag-btn-${l.id}`} onClick={() => flagProblem(l)}
                      disabled={flagBusy === l.id}
                      className="mirzapur-btn-secondary inline-flex items-center gap-1">
                      <AlertTriangle size={14} /> {t("Flag a problem", "समस्या दर्ज करें")}
                    </button>
                  </div>
                ) : (
                  <UpdatePanel line={l} token={token} lang={lang}
                    onDone={submitDone}
                    onCancel={() => setOpenId("")} />
                )}
              </div>
            );
          })}
          {data.lines.length === 0 && (
            <div className="mirzapur-card p-8 text-center" data-testid="portal-empty">
              <div className="mirzapur-title text-lg font-bold">{t("No open lines", "कोई लंबित लाइन नहीं")}</div>
              <div className="text-xs text-[#7C8E9E] mt-1">
                {t("You have no outstanding lines with HAG right now. शुक्रिया.",
                   "अभी HAG के साथ आपकी कोई लंबित लाइन नहीं है। शुक्रिया।")}
              </div>
            </div>
          )}
        </div>

        <footer className="mt-10 pb-6 text-center">
          <div className="mirzapur-stripe max-w-xs mx-auto" />
          <div className="mt-3 text-[10px] tracking-[0.3em] uppercase text-[#7C8E9E]">
            Handloom · Bhadohi · Est. Since 1975
          </div>
          <div className="mt-1 text-[10px] text-[#7C8E9E]">
            {t("Contact HAG for a new link if expired.", "समाप्ति पर HAG से नया लिंक लें।")}
          </div>
        </footer>
      </div>
    </div>
  );
}
